import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, delay, fork, put, select, takeEvery } from 'redux-saga/effects';
import { ISwitchUser, switchUser } from 'core/actions/product/state';
import { EntityTypes } from 'constants/entities';
import { tokenRequest } from 'core/services/product';
import { Mode } from 'core/slices/session';
import { updatePlayerState } from 'core/slices/player';
import { RootState } from 'types';
import { JWT_TOKEN, REFRESH_TOKEN } from '../../../../constants';
import packageJson from '../../../../../package.json';

/**
 * Rebuild the player for whoever just signed in.
 *
 * `switchUser` has already dropped the previous user's queue, register and
 * position, so the player is now holding a course link with nothing behind it.
 * Responses that carry their own player response (the token route, a dialogue
 * sign-in) refill it themselves; a plain sign-in — the in-player login node, an
 * admin signing in over a learner, a refresh that validates a different JWT —
 * does not, so the token has to be asked for again. That round trip is also the
 * only thing that gets the server's view of what *this* user is assigned: the
 * old queue is what was putting nodes such as payment in front of learners who
 * were never assigned them.
 */
function* switchUserSaga(action: PayloadAction<ISwitchUser>) {
  if (action.payload.carriesQueue) return;

  const mode: Mode = yield select((state: RootState) => state.session.mode);
  const token: string = yield select((state: RootState) => state.state[EntityTypes.TOKEN]);

  if (mode !== Mode.LEARNING || !token) return;

  // `switchUser` is dispatched ahead of the response that carried the new
  // identity, so that response's JWT has not been written to storage yet. Yield
  // once so the sign-in finishes landing before the token is re-requested with it.
  yield delay(0);

  const jwt = yield call([localStorage, localStorage.getItem], JWT_TOKEN);
  const refresh = yield call([localStorage, localStorage.getItem], REFRESH_TOKEN);
  const deviceId = yield select((state: RootState) => state.session.deviceId);

  yield put(yield call(updatePlayerState, { paused: true, culprit: 'switchUserSaga' }));
  yield put(
    yield call(tokenRequest, {
      token,
      jwt,
      refresh,
      version: packageJson.version,
      deviceId,
    }),
  );
}

function* watchSwitchUser() {
  yield takeEvery(switchUser, switchUserSaga);
}

export function* userSaga() {
  yield all([fork(watchSwitchUser)]);
}
